import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Layout,
  PageTitle,
  RoundedCheckIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { useStore } from "../store/globalState";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { APP_ROUTES } from "../constants";
import { useLiveExitUrl } from "../hooks/useLiveExitUrl";
import {
  appendAnalyticsHandoff,
  getAnalyticsSession,
  getEvents,
  safeCapture,
} from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";

/**
 * Final cash-deposit confirmation screen. Check-circle hero matches the
 * fund-request "transfer processed" success mark, plus a single footer CTA.
 */
export const DepositOnTheWayPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { redirectTo } = useRouteWithToken();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors, backgroundIcon } =
    colorConfig;

  // The link carries the URL of the host app to return to (e.g. the
  // fund-request home that launched this flow). When present, the return CTA
  // leaves the widget; otherwise it falls back to the internal landing screen.
  const callbackUrl = useLiveExitUrl();

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("done"));
  }, []);

  const handleDone = () => {
    if (callbackUrl) {
      const { sessionId, distinctId } = getAnalyticsSession();
      const handoffUrl =
        sessionId && distinctId
          ? appendAnalyticsHandoff(callbackUrl, { sessionId, distinctId })
          : callbackUrl;
      window.location.href = handoffUrl;
      return;
    }
    redirectTo(APP_ROUTES.CASH_DEPOSIT_LANDING_PAGE);
  };

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <RoundedCheckIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("cashDeposit.onTheWayHighlight")}
              highlightColor={bodyCopy}
              secudnary={t("cashDeposit.onTheWaySecondary")}
              secudnaryColor={bodyCopy}
            />
          </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={handleDone}
          >
            {t("cashDeposit.done")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
